import { Box, Button, Stack, Typography, Skeleton } from '@mui/material';
import { useFormikContext } from 'formik';
import { Buildings2 } from 'iconsax-react';
import { type FC, type ReactElement, useMemo, useState } from 'react';

import { DEBOUNCE_TIME } from '@core/constants/common';
import { useDebounceValue } from '@core/hooks/useDebounceValue';
import type { ActualsPlantPerformanceSummary } from '@core/types/api/ActualsPlantPerformanceSummary/ActualsPlantPerformanceSummary';
import Enums from '@core/types/api/Enums';
import { List } from '@core/types/list';
import { hasStringValue } from '@core/utils/equality';
import { EmptyState, SkeletonList } from '@web/components';
import InputSearch from '@web/components/InputSearch/InputSearch';
import { MultiSelectFilter } from '@web/components/MultiSelectFilter/MultiSelectFilter';
import { useSlabInfiniteQuery } from '@web/hooks/useSlabInfiniteQuery';
import { useSlabQuery } from '@web/hooks/useSlabQuery';
import type { FilterByOpts } from '@web/utils/ApiClient';
import type { BacklogFiltersFormikType } from '@web/utils/backlog';
import { backlogMarketQueryFilters } from '@web/utils/backlogFilters';
import { productCategoryFilter } from '@web/utils/productCategoryFilter';
import { getDivisionOptsFromList } from '@web/utils/tableFilters';

import {
  LocationPerformanceCard,
  LocationPerformanceCardSkeleton,
} from './LocationPerformanceCard';

const PERFORMANCES_PER_PAGE = 12;

const buildFilters = (
  selectedMarkets: string[],
  selectedDivisions: string[],
  searchText: string,
): FilterByOpts<ActualsPlantPerformanceSummary>[] => {
  const filters: FilterByOpts<ActualsPlantPerformanceSummary>[] = [];

  if (selectedDivisions.length > 0) {
    filters.push({
      name: 'divisionId',
      operation: Enums.FilterOperation.Equals,
      values: selectedDivisions,
    });
  }

  // Market comes from the page-level Backlog filter, not local state.
  filters.push(...backlogMarketQueryFilters<ActualsPlantPerformanceSummary>(selectedMarkets));

  const trimmedSearchText = searchText.trim().toLowerCase();
  if (hasStringValue(trimmedSearchText)) {
    filters.push({
      operation: Enums.FilterOperation.Search,
      values: [trimmedSearchText],
    });
  }

  return filters;
};

export const LocationPerformance: FC<{ previewMode?: boolean }> = ({ previewMode }) => {
  const { values } = useFormikContext<BacklogFiltersFormikType>();
  const selectedMarkets = values.markets;
  const [searchText, setSearchText] = useState('');
  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([]);
  const debouncedSearch = useDebounceValue(searchText, DEBOUNCE_TIME);

  const {
    data,
    isLoading: isLoadingPlantPerformance,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSlabInfiniteQuery(
    'GET plants performance',
    {
      queryParams: {
        filterBy: [
          ...buildFilters(selectedMarkets, selectedDivisions, debouncedSearch),
          ...(values.productCategory !== null
            ? [productCategoryFilter<ActualsPlantPerformanceSummary>(values.productCategory)]
            : []),
        ],
      },
    },
    PERFORMANCES_PER_PAGE,
  );
  const allItems = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);

  const { data: marketList } = useSlabQuery('GET markets', {});
  const { data: divisionList, isLoading: isLoadingDivisionList } = useSlabQuery(
    'GET divisions',
    {},
  );

  const divisionOpts = useMemo(() => {
    return getDivisionOptsFromList(divisionList ?? List.zero());
  }, [divisionList]);

  const isMarketsHaveDivision = useMemo(() => {
    return marketList?.items.some((m) => m.divisionId !== null);
  }, [marketList]);

  const renderCards = (): ReactElement | ReactElement[] => {
    if (isLoadingPlantPerformance) {
      return (
        <SkeletonList>
          <LocationPerformanceCardSkeleton />
        </SkeletonList>
      );
    }

    if (allItems.length > 0) {
      return allItems.map((loc) => <LocationPerformanceCard data={loc} key={loc.name} />);
    }

    return (
      <Box width='100%'>
        <EmptyState
          icon={Buildings2}
          title='No locations found'
          description='Please check your filters and try again.'
        />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        marginBottom: 2,
        position: 'relative',
      }}
    >
      <Typography variant='h4' mb={2}>
        Location Performance
      </Typography>
      <Stack direction='row' spacing={2} mb={3}>
        <InputSearch
          disabled={previewMode}
          placeholder='Search location...'
          onChange={(v): void => setSearchText(v)}
        />
        {isMarketsHaveDivision &&
          (isLoadingDivisionList ? (
            <Skeleton variant='rounded' width={150} height={55} />
          ) : (
            <MultiSelectFilter
              disabled={previewMode}
              label='Select division'
              options={divisionOpts}
              selectedIDs={selectedDivisions}
              onChange={(selectedDivisionIDs): void => {
                setSelectedDivisions(selectedDivisionIDs);
              }}
            />
          ))}
      </Stack>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          pb: 1,
        }}
      >
        {renderCards()}
      </Box>
      {!previewMode && hasNextPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant='outlined'
            onClick={(): Promise<unknown> => fetchNextPage()}
            disabled={isFetchingNextPage}
            sx={{ minWidth: 120 }}
          >
            {isFetchingNextPage ? 'Loading...' : 'Load More'}
          </Button>
        </Box>
      )}
    </Box>
  );
};
